import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

/* ────────────────────────────────────────────────────
   DayOne Landing Page — Lavender Dream
   ──────────────────────────────────────────────────── */

const IDEAS = [
  {
    category: "Health·SaaS",
    name: "PatientFlow",
    tagline: "AI scheduling that reduces no-shows by 40%",
    searches: "12.4K",
    mvp: "3 weeks",
    competitors: 8,
    arr: "$180K",
  },
  {
    category: "Fintech·Creator",
    name: "CreatorPayroll",
    tagline: "Automated payouts for creator economy teams",
    searches: "8.2K",
    mvp: "4 weeks",
    competitors: 5,
    arr: "$240K",
  },
  {
    category: "Restaurant·AI",
    name: "FoodCostAI",
    tagline: "Real-time food cost tracking with waste prediction",
    searches: "6.8K",
    mvp: "2 weeks",
    competitors: 12,
    arr: "$120K",
  },
  {
    category: "Legal·AI",
    name: "ContractScan",
    tagline: "AI contract review for small law firms",
    searches: "15.1K",
    mvp: "5 weeks",
    competitors: 6,
    arr: "$300K",
  },
];

const AGENTS = [
  {
    name: "Max",
    role: "Marketing Agent",
    desc: "Automatically posts to Twitter/X, Reddit, and LinkedIn based on your app's value props.",
    tags: ["Twitter/X", "Reddit", "LinkedIn"],
    color: "#7c6af0",
  },
  {
    name: "Sage",
    role: "Support Agent",
    desc: "Embedded chat widget answers user questions. Escalates to Telegram when needed.",
    tags: ["Chat Widget", "Telegram Alerts"],
    color: "#22c55e",
  },
  {
    name: "Hugo",
    role: "Growth Agent",
    desc: "Reads your PostHog analytics and sends a weekly digest with actionable insights.",
    tags: ["PostHog", "Weekly Digest"],
    color: "#f59e0b",
  },
  {
    name: "Rex",
    role: "Fix Agent",
    desc: "Monitors Vercel error logs. Diagnoses issues and suggests fixes before you notice.",
    tags: ["Vercel Logs", "Auto-Diagnosis"],
    color: "#ef4444",
  },
];

const FILTERS = ["All ideas", "SaaS", "Fintech", "Health", "Restaurant", "Legal", "Creator", "E-commerce"];

/* ────────────────────────────────────────────────────
   Styles
   ──────────────────────────────────────────────────── */
const styles = `
  @keyframes fadeUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.7;
      transform: scale(1.15);
    }
  }

  @keyframes scrollLine {
    0% {
      transform: scaleY(0);
      transform-origin: top;
    }
    50% {
      transform: scaleY(1);
      transform-origin: top;
    }
    51% {
      transform-origin: bottom;
    }
    100% {
      transform: scaleY(0);
      transform-origin: bottom;
    }
  }

  .fade-up {
    animation: fadeUp 0.6s ease-out forwards;
    opacity: 0;
  }

  .fade-up-delay-1 { animation-delay: 0.1s; }
  .fade-up-delay-2 { animation-delay: 0.2s; }
  .fade-up-delay-3 { animation-delay: 0.3s; }
  .fade-up-delay-4 { animation-delay: 0.4s; }
  .fade-up-delay-5 { animation-delay: 0.5s; }

  .pulse-dot {
    animation: pulse 2s ease-in-out infinite;
  }

  .scroll-line {
    animation: scrollLine 2s ease-in-out infinite;
  }

  .reveal {
    opacity: 0;
    transform: translateY(30px);
    transition: opacity 0.6s ease-out, transform 0.6s ease-out;
  }

  .reveal.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .frosted-glass {
    background: rgba(255, 255, 255, 0.62);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.8);
  }

  .frosted-nav {
    background: rgba(237, 233, 248, 0.82);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.55);
  }

  .gradient-bg {
    background:
      radial-gradient(ellipse 90% 70% at 5% -5%, rgba(175, 158, 252, 0.6) 0%, transparent 55%),
      radial-gradient(ellipse 70% 60% at 95% 8%, rgba(218, 148, 238, 0.45) 0%, transparent 52%),
      radial-gradient(ellipse 55% 45% at 50% 105%, rgba(155, 195, 245, 0.35) 0%, transparent 55%),
      radial-gradient(ellipse 45% 35% at 85% 55%, rgba(245, 158, 182, 0.28) 0%, transparent 48%),
      #ede9f5;
  }

  .font-instrument {
    font-family: 'Instrument Serif', serif;
  }

  .font-dm {
    font-family: 'DM Sans', sans-serif;
  }
`;

export default function LandingPage() {
  const navigate = useNavigate();
  const revealRefs = useRef<HTMLElement[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    document.querySelectorAll(".reveal").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{styles}</style>
      <div className="gradient-bg min-h-screen font-dm">
        {/* ═══════════════════════════════════════════════════
            SECTION 1 — Nav
           ═══════════════════════════════════════════════════ */}
        <nav className="frosted-nav fixed top-0 left-0 right-0 z-50 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <Link to="/" className="font-instrument text-[22px] text-gray-900">
              DayOne
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/ideas" className="text-gray-700 hover:text-gray-900 text-sm font-medium">
                Ideas
              </Link>
              <Link to="/create" className="text-gray-700 hover:text-gray-900 text-sm font-medium">
                Builder
              </Link>
              <a href="#agents" className="text-gray-700 hover:text-gray-900 text-sm font-medium">
                Agents
              </a>
              <a href="#pricing" className="text-gray-700 hover:text-gray-900 text-sm font-medium">
                Pricing
              </a>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/signin" className="text-gray-700 hover:text-gray-900 text-sm font-medium">
                Log in
              </Link>
              <button
                onClick={() => navigate("/create")}
                className="bg-gray-900 text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
              >
                Start free
              </button>
            </div>
          </div>
        </nav>

        {/* ═══════════════════════════════════════════════════
            SECTION 2 — Hero
           ═══════════════════════════════════════════════════ */}
        <section className="min-h-screen flex flex-col items-center justify-center px-6 pt-20 pb-16 relative">
          {/* Badge */}
          <div className="fade-up fade-up-delay-1 flex items-center gap-2 bg-white/60 backdrop-blur-sm border border-white/80 rounded-full px-4 py-2 mb-8">
            <span className="w-2 h-2 bg-green-500 rounded-full pulse-dot" />
            <span className="text-sm text-gray-700">500+ validated startup ideas · Updated weekly</span>
          </div>

          {/* Icon */}
          <div
            className="fade-up fade-up-delay-2 w-[92px] h-[92px] rounded-[26px] mb-8 flex items-center justify-center"
            style={{ background: "linear-gradient(135deg, #a898f8 0%, #cc82e8 100%)" }}
          >
            <div className="grid grid-cols-2 gap-2">
              <div className="w-5 h-5 bg-white rounded-sm" />
              <div className="w-5 h-5 bg-white rounded-sm" />
              <div className="w-5 h-5 bg-white rounded-sm" />
              <div className="w-5 h-5 bg-white rounded-sm" />
            </div>
          </div>

          {/* H1 */}
          <h1 className="fade-up fade-up-delay-3 font-instrument text-center">
            <span className="block text-[clamp(36px,8vw,86px)] text-gray-900 leading-[1.05]">
              Anyone can build a startup now.
            </span>
            <span
              className="block text-[clamp(36px,8vw,86px)] leading-[1.05] italic"
              style={{ color: "#7c6af0" }}
            >
              We made sure of it.
            </span>
          </h1>

          {/* Subheadline */}
          <p className="fade-up fade-up-delay-4 text-center text-gray-600 text-lg md:text-xl font-light max-w-3xl mt-8 leading-relaxed">
            DayOne finds you a validated startup idea, builds the full-stack app with AI, deploys it live,
            and runs it for you — no code, no co-founder, no agency.
          </p>

          {/* CTAs */}
          <div className="fade-up fade-up-delay-5 flex flex-col sm:flex-row items-center gap-4 mt-10">
            <button
              onClick={() => navigate("/create")}
              className="bg-gray-900 text-white px-8 py-4 rounded-full text-base font-medium hover:bg-gray-800 transition-colors flex items-center gap-2"
            >
              Start building free
              <span>→</span>
            </button>
            <button
              onClick={() => navigate("/ideas")}
              className="bg-transparent text-gray-700 px-8 py-4 rounded-full text-base font-medium border border-gray-300 hover:border-gray-400 transition-colors flex items-center gap-2"
            >
              Browse 500+ ideas
              <span>→</span>
            </button>
          </div>

          {/* Social Proof */}
          <div className="fade-up fade-up-delay-5 flex items-center gap-3 mt-12">
            <div className="flex -space-x-3">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-10 h-10 rounded-full border-2 border-white"
                  style={{
                    background: `linear-gradient(135deg, hsl(${i * 60}, 70%, 70%) 0%, hsl(${i * 60 + 40}, 70%, 60%) 100%)`,
                  }}
                />
              ))}
            </div>
            <span className="text-gray-600 text-sm">127 founders building right now</span>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
            <span className="text-xs text-gray-500 uppercase tracking-wider">Scroll</span>
            <div className="w-px h-8 bg-gray-300 overflow-hidden">
              <div className="w-full h-full bg-gray-600 scroll-line" />
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════
            SECTION 3 — Three Pillars
           ═══════════════════════════════════════════════════ */}
        <section className="py-24 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="frosted-glass rounded-3xl overflow-hidden reveal">
              <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/50">
                {/* Pillar 1 */}
                <div className="p-10 relative">
                  <span className="font-instrument text-[80px] text-gray-200 absolute top-4 right-6">01</span>
                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-6">
                      <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <h3 className="font-instrument text-2xl text-gray-900 mb-3">Idea Engine</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      500+ validated startup ideas with real demand signals, full business plans, and competitor analysis.
                    </p>
                    <span className="inline-block bg-purple-100 text-purple-700 text-xs font-medium px-3 py-1 rounded-full">
                      No competitor has this
                    </span>
                  </div>
                </div>

                {/* Pillar 2 */}
                <div className="p-10 relative">
                  <span className="font-instrument text-[80px] text-gray-200 absolute top-4 right-6">02</span>
                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-6">
                      <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                      </svg>
                    </div>
                    <h3 className="font-instrument text-2xl text-gray-900 mb-3">AI Builder</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      Describe your app in plain English. Get a fully built full-stack app with auth, database, and API in 2 minutes.
                    </p>
                    <span className="inline-block bg-blue-100 text-blue-700 text-xs font-medium px-3 py-1 rounded-full">
                      Competitive with Lovable
                    </span>
                  </div>
                </div>

                {/* Pillar 3 */}
                <div className="p-10 relative">
                  <span className="font-instrument text-[80px] text-gray-200 absolute top-4 right-6">03</span>
                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mb-6">
                      <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <h3 className="font-instrument text-2xl text-gray-900 mb-3">AI Agents</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      4 autonomous agents run your company after deploy: marketing, support, analytics, and error monitoring.
                    </p>
                    <span className="inline-block bg-green-100 text-green-700 text-xs font-medium px-3 py-1 rounded-full">
                      Nobody else has this
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════
            SECTION 4 — Ideas
           ═══════════════════════════════════════════════════ */}
        <section className="py-24 px-6">
          <div className="max-w-6xl mx-auto">
            {/* Section Badge */}
            <div className="flex justify-center mb-6 reveal">
              <span className="bg-purple-100 text-purple-700 text-xs font-medium px-4 py-1.5 rounded-full">
                Idea engine
              </span>
            </div>

            {/* H2 */}
            <h2 className="font-instrument text-4xl md:text-5xl text-center text-gray-900 mb-8 reveal">
              500+ startup ideas, fully validated.
            </h2>

            {/* Filters */}
            <div className="flex flex-wrap justify-center gap-2 mb-12 reveal">
              {FILTERS.map((filter, i) => (
                <button
                  key={filter}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    i === 0
                      ? "bg-gray-900 text-white"
                      : "bg-white/60 text-gray-700 hover:bg-white/80"
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>

            {/* Ideas Grid */}
            <div className="grid md:grid-cols-2 gap-6 reveal">
              {IDEAS.map((idea) => (
                <div key={idea.name} className="frosted-glass rounded-2xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <span className="bg-gray-100 text-gray-700 text-xs font-medium px-3 py-1 rounded-full">
                      {idea.category}
                    </span>
                    <span className="text-green-600 font-semibold">{idea.arr} ARR</span>
                  </div>
                  <h3 className="font-instrument text-2xl text-gray-900 mb-2">{idea.name}</h3>
                  <p className="text-gray-600 text-sm mb-6">{idea.tagline}</p>
                  <div className="flex items-center gap-6 mb-6 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      {idea.searches}/mo
                    </span>
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {idea.mvp} MVP
                    </span>
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      {idea.competitors} competitors
                    </span>
                  </div>
                  <button
                    onClick={() => navigate("/create")}
                    className="bg-gray-900 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
                  >
                    Build this →
                  </button>
                </div>
              ))}
            </div>

            {/* Browse All Link */}
            <div className="text-center mt-10 reveal">
              <Link
                to="/ideas"
                className="text-gray-700 hover:text-gray-900 font-medium inline-flex items-center gap-2"
              >
                Browse all 500+ ideas
                <span>→</span>
              </Link>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════
            SECTION 5 — Builder Preview
           ═══════════════════════════════════════════════════ */}
        <section className="py-24 px-6">
          <div className="max-w-6xl mx-auto">
            {/* Section Badge */}
            <div className="flex justify-center mb-6 reveal">
              <span className="bg-blue-100 text-blue-700 text-xs font-medium px-4 py-1.5 rounded-full">
                AI builder
              </span>
            </div>

            {/* H2 */}
            <h2 className="font-instrument text-4xl md:text-5xl text-center text-gray-900 mb-12 reveal">
              Full-stack app in under 2 minutes.
            </h2>

            {/* Builder Mockup */}
            <div className="frosted-glass rounded-2xl overflow-hidden shadow-2xl reveal">
              {/* Title Bar */}
              <div className="bg-gray-100/80 px-4 py-3 flex items-center justify-between border-b border-white/50">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex items-center gap-4">
                  {["Preview", "Files", "Database", "Analytics"].map((tab, i) => (
                    <span
                      key={tab}
                      className={`text-xs font-medium ${i === 0 ? "text-gray-900" : "text-gray-500"}`}
                    >
                      {tab}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1.5 text-xs">
                    <span className="w-2 h-2 bg-green-500 rounded-full" />
                    Ready · 43 files
                  </span>
                  <button
                    className="text-white text-xs font-medium px-4 py-1.5 rounded-full"
                    style={{ background: "linear-gradient(135deg, #7c6af0 0%, #cc82e8 100%)" }}
                  >
                    Publish →
                  </button>
                </div>
              </div>

              {/* Builder Content */}
              <div className="grid grid-cols-12 min-h-[480px]">
                {/* File Sidebar */}
                <div className="col-span-2 bg-gray-50/50 border-r border-white/50 p-4">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Files</div>
                  {["App.tsx", "Shell.tsx", "RecipeCard.tsx", "AddRecipe.tsx", "utils.ts", "index.css"].map((file) => (
                    <div key={file} className="text-xs text-gray-700 py-1 px-2 rounded hover:bg-white/50 cursor-pointer">
                      {file}
                    </div>
                  ))}
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-6 mb-3">Database</div>
                  <div className="text-xs text-gray-700 py-1 px-2">recipes</div>
                  <div className="text-xs text-gray-700 py-1 px-2">users</div>
                  <div className="text-xs text-gray-700 py-1 px-2">favorites</div>
                </div>

                {/* Preview Window */}
                <div className="col-span-6 bg-white/30 p-6 flex items-center justify-center">
                  <div className="w-full max-w-sm bg-white rounded-xl shadow-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="h-4 w-24 bg-gray-200 rounded" />
                      <div className="h-8 w-8 bg-gray-100 rounded-full" />
                    </div>
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-gray-50 rounded-lg p-3 flex gap-3">
                          <div className="w-16 h-16 bg-gray-200 rounded-lg flex-shrink-0" />
                          <div className="flex-1">
                            <div className="h-3 w-24 bg-gray-200 rounded mb-2" />
                            <div className="h-2 w-full bg-gray-100 rounded mb-1" />
                            <div className="h-2 w-2/3 bg-gray-100 rounded" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Chat Panel */}
                <div className="col-span-4 bg-gray-50/50 border-l border-white/50 p-4 flex flex-col">
                  <div className="flex items-center gap-2 mb-4">
                    <button className="text-xs font-medium bg-gray-900 text-white px-3 py-1 rounded-full">Build</button>
                    <button className="text-xs font-medium text-gray-500 px-3 py-1">Chat</button>
                  </div>
                  <div className="flex-1 space-y-3 text-xs">
                    <div className="bg-white rounded-lg p-3">
                      <span className="text-gray-500">You:</span>
                      <p className="text-gray-700">Build a recipe app with search and favorites</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3">
                      <span className="text-purple-600">DayOne:</span>
                      <p className="text-gray-700">Building your recipe app...</p>
                      <div className="mt-2 space-y-1">
                        {["Create project structure", "Build recipe components", "Add search functionality", "Set up favorites"].map((item, i) => (
                          <div key={item} className="flex items-center gap-2">
                            <svg className="w-3 h-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-gray-600">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="bg-white rounded-lg border border-gray-200 p-3">
                      <input
                        type="text"
                        placeholder="Describe what to build..."
                        className="w-full text-xs text-gray-700 bg-transparent outline-none"
                        disabled
                      />
                    </div>
                    <div className="flex gap-2 mt-2">
                      {["Add auth", "Dark mode", "Mobile nav"].map((action) => (
                        <span key={action} className="text-[10px] bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                          {action}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════
            SECTION 6 — Agents
           ═══════════════════════════════════════════════════ */}
        <section id="agents" className="py-24 px-6">
          <div className="max-w-6xl mx-auto">
            {/* Section Badge */}
            <div className="flex justify-center mb-6 reveal">
              <span className="bg-green-100 text-green-700 text-xs font-medium px-4 py-1.5 rounded-full">
                AI agents
              </span>
            </div>

            {/* H2 */}
            <h2 className="font-instrument text-4xl md:text-5xl text-center text-gray-900 mb-12 reveal">
              Your app runs itself after you ship.
            </h2>

            {/* Agents Grid */}
            <div className="grid md:grid-cols-2 gap-6 reveal">
              {AGENTS.map((agent) => (
                <div key={agent.name} className="frosted-glass rounded-2xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-instrument text-xl flex-shrink-0"
                      style={{ backgroundColor: agent.color }}
                    >
                      {agent.name[0]}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-instrument text-xl text-gray-900">{agent.name}</h3>
                        <span className="text-xs text-gray-500">{agent.role}</span>
                        <div className="flex items-center gap-1.5 ml-auto">
                          <span className="w-2 h-2 bg-green-500 rounded-full pulse-dot" />
                          <span className="text-xs text-green-600">Live</span>
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm mb-4">{agent.desc}</p>
                      <div className="flex flex-wrap gap-2">
                        {agent.tags.map((tag) => (
                          <span key={tag} className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════
            SECTION 7 — Pricing
           ═══════════════════════════════════════════════════ */}
        <section id="pricing" className="py-24 px-6">
          <div className="max-w-5xl mx-auto">
            {/* Section Badge */}
            <div className="flex justify-center mb-6 reveal">
              <span className="bg-orange-100 text-orange-700 text-xs font-medium px-4 py-1.5 rounded-full">
                Pricing
              </span>
            </div>

            {/* H2 */}
            <h2 className="font-instrument text-4xl md:text-5xl text-center text-gray-900 mb-4 reveal">
              Flat pricing. No credit anxiety.
            </h2>
            <p className="text-center text-gray-600 mb-12 reveal">
              Lovable charges per message. We charge one flat monthly fee. Build as much as you want.
            </p>

            {/* Pricing Grid */}
            <div className="grid md:grid-cols-3 gap-6 reveal">
              {/* Free */}
              <div className="frosted-glass rounded-2xl p-8">
                <h3 className="font-instrument text-2xl text-gray-900 mb-2">Free</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="font-instrument text-4xl text-gray-900">$0</span>
                  <span className="text-gray-500">/month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {["Browse all ideas", "3 builds per month", "Preview only (no deploy)"].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                      <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate("/create")}
                  className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  Get started
                </button>
              </div>

              {/* Pro */}
              <div className="bg-gray-900 rounded-2xl p-8 relative overflow-hidden">
                <div
                  className="absolute inset-0 opacity-30"
                  style={{
                    background: "radial-gradient(circle at 50% 0%, #7c6af0 0%, transparent 50%)",
                  }}
                />
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-instrument text-2xl text-white">Pro</h3>
                    <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">Popular</span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="font-instrument text-4xl text-white">$29</span>
                    <span className="text-gray-400">/month</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {[
                      "Unlimited builds",
                      "Deploy to production",
                      "Custom domains",
                      "All 4 AI agents",
                      "Full business plans",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-gray-300">
                        <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => navigate("/create")}
                    className="w-full bg-white text-gray-900 px-6 py-3 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors"
                  >
                    Start Pro free for 7 days
                  </button>
                </div>
              </div>

              {/* Teams */}
              <div className="frosted-glass rounded-2xl p-8">
                <h3 className="font-instrument text-2xl text-gray-900 mb-2">Teams</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="font-instrument text-4xl text-gray-900">$79</span>
                  <span className="text-gray-500">/month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {["Everything in Pro", "5 team seats", "Shared workspaces", "Priority support"].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                      <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate("/create")}
                  className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  Contact sales
                </button>
              </div>
            </div>

            {/* Note */}
            <p className="text-center text-gray-500 text-sm mt-8 reveal">
              No credit card required. No credits, no overages, no surprises.
            </p>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════
            SECTION 8 — Final CTA
           ═══════════════════════════════════════════════════ */}
        <section className="py-24 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-900 rounded-3xl p-12 md:p-16 text-center relative overflow-hidden reveal">
              <div
                className="absolute inset-0 opacity-40"
                style={{
                  background: "radial-gradient(circle at 50% 0%, #7c6af0 0%, transparent 50%), radial-gradient(circle at 80% 80%, #cc82e8 0%, transparent 40%)",
                }}
              />
              <div className="relative z-10">
                <h2 className="font-instrument text-3xl md:text-5xl text-white mb-6 leading-tight">
                  Start with an idea. Ship an app.
                  <br />
                  <span className="italic" style={{ color: "#a898f8" }}>Let agents run it.</span>
                </h2>
                <p className="text-gray-400 max-w-xl mx-auto mb-10">
                  The only platform that takes you from zero idea to a fully running company — without writing a single line of code.
                </p>
                <button
                  onClick={() => navigate("/create")}
                  className="bg-white text-gray-900 px-8 py-4 rounded-full text-base font-medium hover:bg-gray-100 transition-colors"
                >
                  Start building free →
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════
            SECTION 9 — Footer
           ═══════════════════════════════════════════════════ */}
        <footer className="py-8 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="frosted-glass rounded-2xl px-8 py-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <Link to="/" className="font-instrument text-lg text-gray-900">
                  DayOne
                </Link>
                <div className="flex items-center gap-6 text-sm text-gray-600">
                  <Link to="/ideas" className="hover:text-gray-900">Ideas</Link>
                  <a href="#pricing" className="hover:text-gray-900">Pricing</a>
                  <a href="https://docs.usedayone.net" className="hover:text-gray-900" target="_blank" rel="noopener noreferrer">Docs</a>
                  <a href="https://twitter.com/usedayone" className="hover:text-gray-900" target="_blank" rel="noopener noreferrer">Twitter</a>
                  <Link to="/privacy" className="hover:text-gray-900">Privacy</Link>
                </div>
                <span className="text-sm text-gray-500">© 2024 DayOne</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
